import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './TariffsTab.css';

const API_URL = process.env.REACT_APP_API_URL || '';

function TariffsTab() {
  const [tariffs, setTariffs] = useState([]);
  const [subscriptions, setSubscriptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editingTariff, setEditingTariff] = useState(null);
  const [formData, setFormData] = useState({
    subscription_id: '',
    periods: [{ period: '', price: '' }]
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [tariffsRes, subscriptionsRes] = await Promise.all([
        axios.get(`${API_URL}/api/tariffs`),
        axios.get(`${API_URL}/api/subscriptions`)
      ]);
      setTariffs(tariffsRes.data);
      setSubscriptions(subscriptionsRes.data);
      setError(null);
    } catch (err) {
      setError('Ошибка при загрузке данных: ' + (err.response?.data?.detail || err.message));
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      // Фильтруем пустые периоды
      const validPeriods = formData.periods.filter(p => p.period.trim() !== '' && p.price !== '');
      
      if (validPeriods.length === 0) {
        setError('Необходимо добавить хотя бы один период с ценой');
        return;
      }

      // Преобразуем в формат для API
      const periodsPayload = validPeriods.map(p => ({
        period: p.period.trim(),
        price: parseFloat(p.price)
      }));

      const payload = {
        subscription_id: parseInt(formData.subscription_id),
        periods: periodsPayload
      };
      
      if (editingTariff) {
        await axios.put(`${API_URL}/api/tariffs/${editingTariff.id}`, payload);
      } else {
        await axios.post(`${API_URL}/api/tariffs`, payload);
      }
      setShowForm(false);
      setEditingTariff(null);
      setFormData({
        subscription_id: '',
        periods: [{ period: '', price: '' }]
      });
      fetchData();
    } catch (err) {
      // Улучшенная обработка ошибок
      let errorMessage = 'Ошибка при сохранении тарифа: ';
      
      if (err.response?.data) {
        const errorData = err.response.data;
        
        // Если detail это массив (ошибки валидации)
        if (Array.isArray(errorData.detail)) {
          errorMessage += errorData.detail.map(e => {
            if (typeof e === 'object' && e.msg) {
              return `${e.loc?.join('.')}: ${e.msg}`;
            }
            return String(e);
          }).join(', ');
        } 
        // Если detail это строка
        else if (typeof errorData.detail === 'string') {
          errorMessage += errorData.detail;
        }
        // Если detail это объект
        else if (typeof errorData.detail === 'object') {
          errorMessage += JSON.stringify(errorData.detail);
        }
        // Если есть message
        else if (errorData.message) {
          errorMessage += errorData.message;
        }
        else {
          errorMessage += 'Неизвестная ошибка';
        }
      } else if (err.message) {
        errorMessage += err.message;
      } else {
        errorMessage += 'Неизвестная ошибка';
      }
      
      setError(errorMessage);
    }
  };

  const handleEdit = (tariff) => {
    setEditingTariff(tariff);
    
    // Периоды теперь всегда массив объектов с period, price, price_per_month
    let periods = [];
    if (Array.isArray(tariff.periods)) {
      periods = tariff.periods;
    } else {
      try {
        periods = JSON.parse(tariff.periods || '[]');
      } catch {
        periods = [];
      }
    }
    
    // Преобразуем в формат формы (price_per_month вычисляется автоматически на бэкенде)
    const periodsForm = periods.length > 0 
      ? periods.map(p => ({ 
          period: p.period || '', 
          price: (p.price || p.price === 0) ? p.price.toString() : '' 
        }))
      : [{ period: '', price: '' }];
    
    setFormData({
      subscription_id: tariff.subscription_id.toString(),
      periods: periodsForm
    });
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Вы уверены, что хотите удалить этот тариф?')) {
      try {
        await axios.delete(`${API_URL}/api/tariffs/${id}`);
        fetchData();
      } catch (err) {
        // Улучшенная обработка ошибок
        let errorMessage = 'Ошибка при удалении тарифа: ';
        
        if (err.response?.data?.detail) {
          if (Array.isArray(err.response.data.detail)) {
            errorMessage += err.response.data.detail.map(e => 
              typeof e === 'object' && e.msg ? `${e.loc?.join('.')}: ${e.msg}` : String(e)
            ).join(', ');
          } else {
            errorMessage += String(err.response.data.detail);
          }
        } else if (err.message) {
          errorMessage += err.message;
        } else {
          errorMessage += 'Неизвестная ошибка';
        }
        
        setError(errorMessage);
      }
    }
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditingTariff(null);
    setFormData({
      subscription_id: '',
      periods: [{ period: '', price: '' }]
    });
  };

  const addPeriod = () => {
    setFormData({
      ...formData,
      periods: [...formData.periods, { period: '', price: '' }]
    });
  };

  const removePeriod = (index) => {
    const newPeriods = formData.periods.filter((_, i) => i !== index);
    setFormData({
      ...formData,
      periods: newPeriods.length > 0 ? newPeriods : [{ period: '', price: '' }]
    });
  };

  const updatePeriod = (index, field, value) => {
    const newPeriods = [...formData.periods];
    newPeriods[index][field] = value;
    setFormData({ ...formData, periods: newPeriods });
  };

  const getSubscriptionName = (subscriptionId) => {
    const subscription = subscriptions.find(s => s.id === subscriptionId);
    return subscription ? subscription.name : `ID: ${subscriptionId}`;
  };

  const formatPeriods = (periods) => {
    let formattedPeriods = [];
    
    if (Array.isArray(periods)) {
      formattedPeriods = periods;
    } else {
    try {
        formattedPeriods = JSON.parse(periods || '[]');
    } catch {
      return [];
    }
    }
    
    // Убеждаемся, что price_per_month это число
    return formattedPeriods.map(p => ({
      ...p,
      price: typeof p.price === 'number' ? p.price : parseFloat(p.price) || 0,
      price_per_month: p.price_per_month !== undefined && p.price_per_month !== null
        ? (typeof p.price_per_month === 'number' ? p.price_per_month : parseFloat(p.price_per_month))
        : undefined
    }));
  };

  if (loading) {
    return <div className="loading">Загрузка...</div>;
  }

  return (
    <div className="tariffs-tab">
      <div className="tab-header">
        <h3>Управление тарифами</h3>
        {!showForm && (
          <button className="btn btn-primary" onClick={() => setShowForm(true)}>
            + Добавить тариф
          </button>
        )}
      </div>

      {error && <div className="error-message">{error}</div>}

      {showForm && (
        <div className="form-container">
          <h4>{editingTariff ? 'Редактировать тариф' : 'Добавить новый тариф'}</h4>
          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label htmlFor="subscription_id">Подписка *</label>
              <select
                id="subscription_id"
                value={formData.subscription_id}
                onChange={(e) => setFormData({ ...formData, subscription_id: e.target.value })}
                required
                disabled={subscriptions.length === 0}
              >
                <option value="">Выберите подписку</option>
                {subscriptions.map((subscription) => (
                  <option key={subscription.id} value={subscription.id}>
                    {subscription.name}
                  </option>
                ))}
              </select>
              {subscriptions.length === 0 && (
                <small className="form-hint">
                  Сначала создайте подписку
                </small>
              )}
            </div>
            <div className="form-group">
              <label>Периоды и цены *</label>
              {formData.periods.map((periodItem, index) => (
                <div key={index} className="period-row">
                  <input
                    type="text"
                    value={periodItem.period}
                    onChange={(e) => updatePeriod(index, 'period', e.target.value)}
                    placeholder="Период (например, 1 месяц)"
                    className="period-input"
                  />
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={periodItem.price}
                    onChange={(e) => updatePeriod(index, 'price', e.target.value)}
                    placeholder="Цена (руб.)"
                    className="price-input"
                  />
                  {formData.periods.length > 1 && (
                    <button
                      type="button"
                      className="btn-remove"
                      onClick={() => removePeriod(index)}
                      title="Удалить период"
                    >
                      ✕
                    </button>
                  )}
                </div>
              ))}
              <button
                type="button"
                className="btn-add-period"
                onClick={addPeriod}
              >
                + Добавить период
              </button>
              <small className="form-hint">
                Укажите периоды подписки и их стоимость в рублях.
              </small>
            </div>
            <div className="form-actions">
              <button type="submit" className="btn btn-primary" disabled={subscriptions.length === 0}>
                {editingTariff ? 'Сохранить' : 'Создать'}
              </button>
              <button type="button" className="btn btn-secondary" onClick={handleCancel}>
                Отмена
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="table-container">
        <table className="data-table">
          <thead>
            <tr>
              <th>ID</th>
              <th>Подписка</th>
              <th>Периоды и цены</th>
              <th>Действия</th>
            </tr>
          </thead>
          <tbody>
            {tariffs.length === 0 ? (
              <tr>
                <td colSpan="4" className="empty-state">
                  Нет тарифов. Добавьте первый тариф.
                </td>
              </tr>
            ) : (
              tariffs.map((tariff) => {
                const periods = formatPeriods(tariff.periods);
                return (
                  <tr key={tariff.id}>
                    <td>{tariff.id}</td>
                    <td>{getSubscriptionName(tariff.subscription_id)}</td>
                    <td className="periods-cell">
                      <ul className="periods-list">
                        {periods.map((p, idx) => (
                          <li key={idx}>
                            <div className="period-info">
                            <span className="period-name">{p.period}</span>
                              <div className="period-prices">
                            <span className="period-price">{p.price} ₽</span>
                                {p.price_per_month !== undefined && p.price_per_month !== null && (
                                  <span className="period-price-month">
                                    ({typeof p.price_per_month === 'number' 
                                      ? p.price_per_month.toFixed(2) 
                                      : parseFloat(p.price_per_month).toFixed(2)} ₽/мес.)
                                  </span>
                                )}
                              </div>
                            </div>
                          </li>
                        ))}
                      </ul>
                    </td>
                    <td>
                      <button
                        className="btn-icon btn-edit"
                        onClick={() => handleEdit(tariff)}
                        title="Редактировать"
                      >
                        ✏️
                      </button>
                      <button
                        className="btn-icon btn-delete"
                        onClick={() => handleDelete(tariff.id)}
                        title="Удалить"
                      >
                        ✕
                      </button>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default TariffsTab;

